import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fulfillmentsApi, rentalsApi, imagesApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import Modal from '../Modal';
import Navbar from '../Navbar';
import { CARRIER_OPTIONS } from './ShipmentTrackingCard';
import { assetUrl } from '../../utils/assetUrl'

interface AlertModalState {
  isOpen: boolean;
  title: string;
  message: string;
}

interface Photo {
  id: string;
  url: string;            // local data URL for preview
  serverUrl?: string;     // server URL after upload
  timestamp: Date;
  type: 'pre' | 'post';
  uploading: boolean;
  error?: string;
}

export default function EquipmentInspection() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [notes, setNotes] = useState('');
  const [isRenter, setIsRenter] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [rentalId, setRentalId] = useState<string | null>(null);
  const [fulfillmentType, setFulfillmentType] = useState<string | null>(null);
  const [returnTrackingNumber, setReturnTrackingNumber] = useState('');
  const [returnCarrier, setReturnCarrier] = useState(CARRIER_OPTIONS[0].value);
  const [alertModal, setAlertModal] = useState<AlertModalState>({ isOpen: false, title: '', message: '' });
  // Whether this is the pre-pickup or return inspection used to be decided purely from
  // location.state.returnType, set only by RentalDetail.tsx's "Start Return Inspection"
  // button. That state is ephemeral -- reloading the page, opening a bookmarked/
  // notification link, or a new tab loses it, silently falling back to the pre-pickup
  // screen (wrong copy, wrong photoType on submit) on a rental that's already active.
  //
  // First attempt derived this from the fetched rental status (ACTIVE = return) instead,
  // but that's wrong too: PICKED_UP already sets the rental ACTIVE (see
  // FulfillmentService.syncRentalStatus), and the pre-pickup inspection screen is shown
  // immediately after that same transition (PickupTracker.tsx's handleConfirmPickup) --
  // so by the time a renter reaches the *pre-pickup* inspection, the rental is already
  // ACTIVE too. Rental/fulfillment status genuinely can't distinguish "just picked up,
  // documenting now" from "mid-rental, starting a return" for Pickup-type fulfillments --
  // both are simply "PICKED_UP".
  //
  // The one thing that reliably *does* distinguish them is the URL itself: every
  // pre-pickup call site (PickupTracker.tsx, QRCodeScanner.tsx) navigates to
  // `/fulfillment/:id/inspect`, and the only return call site (RentalDetail.tsx's
  // "Start Return Inspection") navigates to `/fulfillment/:id/inspection` -- two
  // distinct routes already wired to this same component in App.tsx. Unlike
  // location.state, the URL path survives a refresh, bookmark, or new tab by
  // definition, so this is correct in every case, not just the common one.
  const isReturnFlow = location.pathname.endsWith('/inspection');
  const [loadingFulfillment, setLoadingFulfillment] = useState(true);
  const draftKey = id ? `inspection-draft-${id}` : null;

  const showAlert = (title: string, message: string) => {
    setAlertModal({ isOpen: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, title: '', message: '' });
  };

  useEffect(() => {
    const qrData = location.state?.qrData;
    if (qrData) {
      setIsRenter(false); // Owner scanning QR
    }
  }, [location.state, user]);

  // Restore any in-progress draft — e.g. after a forced logout or accidental navigation
  // interrupted an inspection that had already uploaded some photos.
  useEffect(() => {
    if (!draftKey) return;
    try {
      const saved = sessionStorage.getItem(draftKey);
      if (!saved) return;
      const draft = JSON.parse(saved) as {
        notes: string;
        photos: Array<{ id: string; serverUrl: string; type: 'pre' | 'post'; timestamp: string }>;
      };
      setNotes(draft.notes || '');
      if (draft.photos?.length) {
        setPhotos(draft.photos.map((p) => ({
          id: p.id,
          url: p.serverUrl,
          serverUrl: p.serverUrl,
          type: p.type,
          timestamp: new Date(p.timestamp),
          uploading: false,
        })));
      }
    } catch {
      // ignore a corrupt draft
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Persist completed uploads + notes as they happen, so losing the tab (forced logout,
  // refresh, accidental back-nav) doesn't throw away photos already uploaded to the server.
  useEffect(() => {
    if (!draftKey) return;
    const uploaded = photos.filter((p) => p.serverUrl && !p.uploading);
    if (uploaded.length === 0 && !notes) {
      sessionStorage.removeItem(draftKey);
      return;
    }
    sessionStorage.setItem(draftKey, JSON.stringify({
      notes,
      photos: uploaded.map((p) => ({ id: p.id, serverUrl: p.serverUrl, type: p.type, timestamp: p.timestamp })),
    }));
  }, [photos, notes, draftKey]);

  useEffect(() => {
    if (!id) return;
    fulfillmentsApi.get(id).then((res) => {
      if (res.data.success) {
        const data = res.data.data;
        setRentalId(data.rental?.id || null);
        setFulfillmentType(data.fulfillmentType || null);
        if (user && data.rental) {
          setIsRenter(user.id === data.rental.renter?.id);
        }
      }
    }).catch(() => {
      // ignore -- rentalId/fulfillmentType just stay null; isReturnFlow itself
      // doesn't depend on this fetch
    }).finally(() => {
      setLoadingFulfillment(false);
    });
  }, [id, user]);

  const startCamera = async () => {
    setCameraError(null);
    try {
      // Laptops typically only have a user-facing webcam; try rear first, then front.
      let mediaStream: MediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
      } catch (err) {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
        });
      }
      setCameraReady(false);
      setStream(mediaStream);
      setShowCamera(true);
      // Attaching the stream to <video> happens in the effect below, once
      // showCamera flips true and the element actually mounts.
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Camera unavailable. You can upload a photo instead.');
      // Fall back to file input
      fileInputRef.current?.click();
    }
  };

  // The <video> element only exists in the DOM once showCamera is true (it's
  // conditionally rendered below), so attaching srcObject inline inside
  // startCamera() ran before the element mounted on first open -- videoRef.current
  // was still null, silently skipping the assignment and leaving a black feed with
  // cameraReady stuck false forever. Doing it here, keyed on `stream`, runs after
  // the render that mounts <video>, so the ref is guaranteed to be attached.
  useEffect(() => {
    if (!stream || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = stream;
    const markReady = () => setCameraReady(true);
    video.addEventListener('loadedmetadata', markReady, { once: true });
    video.addEventListener('playing', markReady, { once: true });
    return () => {
      video.removeEventListener('loadedmetadata', markReady);
      video.removeEventListener('playing', markReady);
    };
  }, [stream]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    setCameraReady(false);
    setCameraError(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !videoRef.current.videoWidth || !cameraReady) {
      setCameraError('Camera not ready. Please try again or upload a photo.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(videoRef.current, 0, 0);
    const photoUrl = canvas.toDataURL('image/jpeg');

    // Convert data URL to File and upload
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `inspection-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const photoId = uuidv4();
      // Add the preview immediately so the user sees a thumbnail even if upload is slow/fails.
      setPhotos(prev => [...prev, {
        id: photoId,
        url: photoUrl,
        timestamp: new Date(),
        type: isRenter ? 'pre' : 'post',
        uploading: true,
      }]);
      stopCamera();
      await uploadPhoto(file, photoId);
    }, 'image/jpeg', 0.85);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      if (event.target?.result) {
        const photoUrl = event.target!.result as string;
        const id = uuidv4();
        setPhotos(prev => [...prev, {
          id,
          url: photoUrl,
          timestamp: new Date(),
          type: isRenter ? 'pre' : 'post',
          uploading: true,
        }]);
        await uploadPhoto(file, id);
      }
    };
    reader.readAsDataURL(file);
  };

  const uploadPhoto = async (file: File, id: string) => {
    try {
      // Goes through the shared `api` instance (not a raw fetch + manual token read) so an
      // expired access token gets silently refreshed and retried instead of failing outright.
      const response = await imagesApi.uploadTemp(file);
      const imageUrl = response.data.images[0].tempUrl;
      setPhotos(prev => prev.map((p) => p.id === id ? { ...p, serverUrl: imageUrl, uploading: false } : p));
    } catch (err: any) {
      console.error('Photo upload failed:', err);
      // Previously this only logged to the console -- the thumbnail (rendered from the
      // local data URL, set before the upload even started) looked identical to a
      // successful one, so a rejected upload (wrong file type, corrupt file, etc.) was
      // silently invisible until the final submit failed with a generic "nothing was
      // uploaded" error. Surfacing the real reason on the thumbnail itself instead.
      const message = err?.response?.data?.error || 'Upload failed. Remove this photo and try another.';
      setPhotos(prev => prev.map((p) => p.id === id ? { ...p, uploading: false, error: message } : p));
    }
  };

  const removePhoto = (id: string) => {
    setPhotos(prev => prev.filter((p) => p.id !== id));
  };

  const handleSubmit = async () => {
    if (photos.length === 0) {
      showAlert('Photo required', 'Please take at least one photo before continuing.');
      return;
    }

    if (photos.some(p => p.uploading)) {
      showAlert('Upload in progress', 'Please wait for photos to finish uploading.');
      return;
    }

    setSubmitting(true);
    try {
      const photoUrls = photos.filter(p => p.serverUrl).map(p => p.serverUrl!);

      if (photoUrls.length === 0) {
        showAlert('Upload failed', 'No uploaded photos to submit. Please remove failed photos and try again.');
        setSubmitting(false);
        return;
      }

      // Upload photos to fulfillment record
      const photoType = isReturnFlow ? 'return' : (isRenter ? 'pre_pickup' : 'post_pickup');
      await fulfillmentsApi.uploadPhotos(id!, {
        photos: photoUrls,
        type: photoType as 'pre_pickup' | 'post_pickup' | 'return',
        notes: notes || undefined,
      });

      if (isReturnFlow) {
        if (!rentalId) {
          showAlert('Missing rental', 'Could not find the rental for this fulfillment. Please contact support.');
          setSubmitting(false);
          return;
        }
        await rentalsApi.return(
          rentalId,
          photoUrls[0],
          returnTrackingNumber.trim() || undefined,
          returnTrackingNumber.trim() ? returnCarrier : undefined
        );
        if (draftKey) sessionStorage.removeItem(draftKey);
        navigate(`/rental/${rentalId}`);
      } else if (fulfillmentType === 'DELIVERY') {
        // Shipping/DoorDash has no in-person handshake -- the owner's part
        // ended when they shipped it, so photos + this status transition is
        // the whole "receipt confirmation" for the renter.
        await fulfillmentsApi.updateStatus(id!, 'DELIVERED');
        if (draftKey) sessionStorage.removeItem(draftKey);
        navigate(`/fulfillment/${id}/shipping`);
      } else {
        // The renter's photos are what let them confirm the pickup: with them uploaded, record
        // their side of the handshake now. (The owner's photos are optional and don't confirm.)
        if (isRenter) await fulfillmentsApi.confirmHandshake(id!);
        if (draftKey) sessionStorage.removeItem(draftKey);
        navigate(`/fulfillment/${id}/handshake`);
      }
    } catch (err: any) {
      console.error('Submit error:', err);
      showAlert('Submission failed', err?.response?.data?.error || 'Failed to submit inspection. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingFulfillment) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-[var(--text-secondary)]">Loading...</div>
        </div>
      </div>
    );
  }

  // The owner can't self-report a return -- only confirm one the renter has already
  // submitted (see RentalDetail.tsx's "Confirm return / complete", gated on status
  // RETURNED). This screen's own entry point (RentalDetail's "Start Return
  // Inspection") is already renter-only, but guard here too since the URL itself
  // isn't -- a stale link or direct navigation shouldn't let the owner submit one.
  if (isReturnFlow && !isRenter) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 max-w-sm text-center">
            <i className="mdi mdi-camera-off-outline text-3xl text-[var(--text-muted)] mb-2"></i>
            <h1 className="text-lg font-bold text-[var(--text-primary)] mb-1">Waiting for the renter</h1>
            <p className="text-sm text-[var(--text-secondary)]">
              Only the renter can start a return inspection. Once they submit return
              photos, you'll be able to confirm the return from the rental page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />
      <div className="container mx-auto px-4 max-w-md py-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <i className="mdi mdi-arrow-left"></i>
            Back
          </button>
          <h1 className="text-xl font-bold text-[var(--text-primary)] mt-2">
            {isReturnFlow ? 'Return Inspection' : fulfillmentType === 'DELIVERY' ? 'Confirm Receipt' : 'Equipment Inspection'}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {isReturnFlow
              ? 'Document the condition of the equipment upon return'
              : fulfillmentType === 'DELIVERY'
                ? 'Document the condition of the item now that it has arrived'
                : isRenter
                  ? 'Document the condition of the equipment before pickup'
                  : 'Verify the equipment condition before handing over'
            }
          </p>
        </div>

        {/* Camera or File Input */}
        {showCamera ? (
          <div className="bg-black rounded-xl overflow-hidden mb-4 relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full aspect-[3/4] object-cover"
            />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
              <button
                onClick={capturePhoto}
                className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-lg"
              >
                <div className="w-12 h-12 rounded-full border-4 border-[var(--forest-green)]"></div>
              </button>
              <button
                onClick={stopCamera}
                className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg"
              >
                <i className="mdi mdi-close text-2xl text-red-600"></i>
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={startCamera}
            className="w-full bg-white rounded-xl border-2 border-dashed border-[var(--border)] p-8 mb-4 hover:border-[var(--forest-green)] transition-colors"
          >
            <div className="flex flex-col items-center">
              <i className="mdi mdi-camera text-4xl text-[var(--text-muted)] mb-2"></i>
              <span className="text-[var(--text-primary)] font-medium">Take Photo</span>
              <span className="text-sm text-[var(--text-muted)]">Tap to open camera</span>
            </div>
          </button>
        )}

        {cameraError && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <i className="mdi mdi-camera-off"></i>
              {cameraError}
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 text-sm font-medium text-[var(--forest-green)] hover:underline"
            >
              Upload a photo instead
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Photo Gallery */}
        {photos.length > 0 && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
            <h3 className="font-medium text-[var(--text-primary)] mb-3">
              Photos ({photos.length})
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((photo, index) => (
                <div key={index} className="relative aspect-square">
                  <img
                    src={assetUrl(photo.url)}
                    alt={`Photo ${index + 1}`}
                    className={`w-full h-full object-cover rounded-lg ${photo.error ? 'opacity-50' : ''}`}
                  />
                  {photo.uploading && (
                    <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                      <i className="mdi mdi-loading mdi-spin text-white text-xl"></i>
                    </div>
                  )}
                  {photo.error && (
                    <div className="absolute inset-0 bg-red-900/40 rounded-lg flex items-center justify-center p-1">
                      <i className="mdi mdi-alert-circle text-white text-2xl"></i>
                    </div>
                  )}
                  <button
                    onClick={() => removePhoto(photo.id)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                  >
                    <i className="mdi mdi-close text-sm"></i>
                  </button>
                </div>
              ))}
              <button
                onClick={startCamera}
                className="aspect-square border-2 border-dashed border-[var(--border)] rounded-lg flex items-center justify-center hover:border-[var(--forest-green)]"
              >
                <i className="mdi mdi-plus text-2xl text-[var(--text-muted)]"></i>
              </button>
            </div>
            {photos.some(p => p.error) && (
              <div className="mt-3 space-y-1">
                {photos.filter(p => p.error).map((p) => (
                  <p key={p.id} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <i className="mdi mdi-alert-circle mr-1"></i>
                    {p.error}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Return tracking number -- shipping/DoorDash only, no carrier is integrated
            yet (FULFILLMENT_PLAN.md #15.4), so this is a self-reported reference for
            the owner to match against whatever tracking the renter arranged off-platform.
            Renter-only screen (see the isReturnFlow && !isRenter guard above), so no
            owner branch here. */}
        {isReturnFlow && fulfillmentType === 'DELIVERY' && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
            <label className="block font-medium text-[var(--text-primary)] mb-2">
              Return tracking number (optional)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={returnCarrier}
                onChange={(e) => setReturnCarrier(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
              >
                {CARRIER_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <input
                type="text"
                value={returnTrackingNumber}
                onChange={(e) => setReturnTrackingNumber(e.target.value)}
                placeholder="e.g. 1Z999AA10123456784"
                maxLength={100}
                className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
              />
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
          <label className="block font-medium text-[var(--text-primary)] mb-2">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Note any existing damage, missing parts, or special instructions..."
            rows={4}
            className="w-full px-3 py-2 border border-[var(--border)] rounded-lg resize-none focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
          />
        </div>

        {/* Submit */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-4">
          <div className="flex items-center gap-3 mb-4 text-sm">
            <i className="mdi mdi-information text-blue-600"></i>
            <span className="text-[var(--text-secondary)]">
              {isReturnFlow
                ? 'This reports the item as returned. The rental is not complete until the owner confirms they have it back.'
                : fulfillmentType === 'DELIVERY'
                  ? 'This confirms you\'ve received the item and marks the rental active.'
                  : 'Both parties will need to confirm pickup after inspection'}
            </span>
          </div>

          {photos.length === 0 && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              <i className="mdi mdi-camera mr-1"></i>
              Take or upload at least one photo to continue.
            </p>
          )}

          <button
            onClick={handleSubmit}
            disabled={photos.length === 0 || submitting}
            className="btn btn-primary w-full"
          >
            {submitting ? (
              <>
                <i className="mdi mdi-loading mdi-spin mr-2"></i>
                Submitting...
              </>
            ) : isReturnFlow ? (
              <>
                <i className="mdi mdi-check-circle mr-2"></i>
                Report as Returned
              </>
            ) : fulfillmentType === 'DELIVERY' ? (
              <>
                <i className="mdi mdi-check-circle mr-2"></i>
                Confirm Receipt
              </>
            ) : (
              <>
                <i className="mdi mdi-check-circle mr-2"></i>
                Complete Inspection
              </>
            )}
          </button>
        </div>
      </div>

      <Modal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        onConfirm={closeAlert}
        onCancel={closeAlert}
        confirmLabel="OK"
        cancelLabel={undefined}
        closeOnBackdrop={true}
      >
        <p>{alertModal.message}</p>
      </Modal>
    </div>
  );
}
